'use client';

import { useRef, type ReactNode } from 'react';
import StageBackdrop from './StageBackdrop';
import { useScrollScene } from './useScrollScene';

/**
 * A stage: one of the homepage's acts, a place pressed into the page (a
 * ground one step below it, in whichever theme the visitor chose), with the
 * blueprint grid behind it.
 *
 * It opens as it arrives — a rounded card widening to the full page — and
 * closes again as it leaves (`.rt-stage`, driven by useScrollScene), so the
 * hand-off to the sections either side is a designed transition rather than
 * a colour change at a line. Whatever plays inside one stage plays in one
 * continuous space: the journey and the listening room share a stage, so the
 * story's last step and its payoff are one scene.
 *
 * It takes no theme of its own: a subtree theme here (it once carried
 * `data-theme="dark"`) paints three acts of a light page dark.
 *
 * The only client part of a stage; what it holds renders wherever it likes.
 */
export default function Stage({ id, testId, children }: {
  id?: string;
  testId?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollScene(ref);
  return (
    <div
      ref={ref}
      id={id}
      data-testid={testId}
      className="rt-scene rt-stage relative scroll-mt-24 text-[var(--rt-ink)]"
    >
      <StageBackdrop />
      {children}
    </div>
  );
}
